import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  CardBody,
  Image,
  Heading,
  Skeleton,
  Center,
  Badge,
} from "@chakra-ui/react";
import { FaBookOpen } from "react-icons/fa";
import { Auth } from "aws-amplify";
import axios from "axios";
import AWS from "aws-sdk";
import { useNavigate } from "react-router-dom";
import { GuideDrawer } from "../Drawer/GuideDrawer";
import { PropertySectionDrawer } from "../Drawer/PropertySectionDrawer";
import {
  buildPropertyGuideItems,
  DIRECTIONS_ITEM_ID,
} from "../Logic/propertyGuideItems";

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

export const GuidesSection = ({ room, imageUrl, onDrawerStateChange }) => {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionOpen, setSectionOpen] = useState(false);

  // Property guide sections come straight off the room record. Leading the
  // preview with them is the point of the merge: this content used to sit
  // behind the "Explore the Property" button that guests were missing.
  const propertyItems = useMemo(
    () => buildPropertyGuideItems(room, imageUrl),
    [room, imageUrl]
  );

  // Directions have their own button in the floating bar, so they don't take
  // a preview slot.
  const previewItems = useMemo(
    () =>
      [
        ...propertyItems.filter((item) => item.id !== DIRECTIONS_ITEM_ID),
        ...guides,
      ].slice(0, 2),
    [propertyItems, guides]
  );

  const handleOpenGuides = useCallback((initialCatalog) => {
    navigate('/guides', { state: { room, imageUrl, initialCatalog } });
  }, [navigate, room, imageUrl]);

  const handleItemClick = useCallback((item) => {
    if (item?.kind === "property") {
      setSelectedSection(item);
      setSectionOpen(true);
      onDrawerStateChange?.(true);
      return;
    }
    setSelectedGuide(item);
    setDrawerOpen(true);
    onDrawerStateChange?.(true);
  }, [onDrawerStateChange]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedGuide(null);
    onDrawerStateChange?.(false);
  }, [onDrawerStateChange]);

  const handleSectionClose = useCallback(() => {
    setSectionOpen(false);
    setSelectedSection(null);
    onDrawerStateChange?.(false);
  }, [onDrawerStateChange]);

  // Memoize the fetchImageUrls function to prevent infinite loops
  const fetchImageUrls = useCallback(async (guidesData) => {
    const newImageUrls = {};
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net"; // Image handler CloudFront URL

    for (const guide of guidesData) {
        // Guides can have no `images` key at all, or an empty array — text-only
        // guides are normal, and the card falls back to a placeholder.
        const imageKey = guide?.images?.[0];
        if (typeof imageKey !== "string" || !imageKey) continue;

        // If it's already a full URL, use it directly
        if (imageKey.startsWith("http")) {

          newImageUrls[guide.id] = imageKey;
          continue;
        }

        // Construct optimized CloudFront URL
        const optimizedUrl = `${cloudfrontBaseUrl}/public/${encodeURIComponent(imageKey)}?width=600&format=webp`;

        newImageUrls[guide.id] = optimizedUrl;
    }


    setImageUrls(newImageUrls);
  }, []);

  const fetchGuides = async () => {
    if (!room?.id) {
      setLoading(false);
      return;
    }

    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      // The preview is a single row of 2 and shares its slots with property
      // sections, so 2 guides is the most it can ever show.
      const response = await axios.get(`https://api.linkbase.ca/guides/by-room/${room.id}?limit=2`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
        }
      });

      const guidesWithCatalogs = response.data?.data?.guidesWithCatalogs || [];
      // Extract guides from the nested structure and merge catalog info
      const guidesData = guidesWithCatalogs.map(item => ({
        ...item.guide,
        catalog: item.catalog
      }));
      
      setGuides(guidesData);
      
      // Image URLs are cosmetic: keep any failure here out of the fetch's
      // error path so a loaded list is never reported as a failed load.
      try {
        await fetchImageUrls(guidesData);
      } catch (imageErr) {
        console.error('Error preparing guide image URLs:', imageErr);
      }
    } catch (err) {
      console.error('Error fetching guides:', err);
      setError('Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuides();
  }, [fetchImageUrls, room?.id]);

  // Same pattern as the Services section: small label on mobile, real heading
  // + "View all" link on desktop only. Phones get the full-width button below
  // the cards instead (see MoreButton).
  const SectionHeader = ({ showLink }) => (
    <Flex justify="space-between" align="center" pb={4}>
      <Text fontSize={{ base: "12px", md: "18px" }} fontWeight={800} color={"white"}>
        Info &amp; Guides
      </Text>
      {showLink && (
        <Button
          variant="link"
          color="#817EFF"
          fontSize="14px"
          fontWeight={700}
          display={{ base: "none", md: "inline-flex" }}
          onClick={() => handleOpenGuides()}
        >
          View all →
        </Button>
      )}
    </Flex>
  );

  const MoreButton = ({ onClick }) => (
    <Button
      onClick={onClick}
      leftIcon={<FaBookOpen />}
      bg={"#817EFF"}
      color="white"
      h="42px"
      w="full"
      display={{ base: "flex", md: "none" }}
      variant="solid"
      borderRadius="10px"
      fontSize="14px"
      _active={{ bg: "#615edc" }}
      _hover={{ bg: "#615edc" }}
    >
      Show More Info &amp; Guides
    </Button>
  );

  // Property sections are local, so they render even while the guides request
  // is in flight or has failed. Only fall back to skeletons / an error when
  // there is genuinely nothing to show.
  if (loading && previewItems.length === 0) {
    return (
      <Box px={{ base: 4, md: 0 }} pt={4} pb={3}>
        <SectionHeader showLink={false} />
        <Flex direction="row" gap={{ base: 4, md: 5 }} mb={4}>
          {[1, 2].map((i) => (
            <Card key={i} flex="1" bg="rgba(255, 255, 255, 0.05)" borderRadius="12px">
              <Skeleton h={{ base: "95px", md: "160px" }} />
              <CardBody p={3} pt={4}>
                <Skeleton height="16px" mb={2} />
                <Skeleton height="12px" />
              </CardBody>
            </Card>
          ))}
        </Flex>
        <MoreButton onClick={() => handleOpenGuides()} />
      </Box>
    );
  }

  if (error && previewItems.length === 0) {
    return (
      <Box px={{ base: 4, md: 0 }} pt={4} pb={3}>
        <SectionHeader showLink={true} />
        <Center h="100px">
          <Text fontSize="12px" color="red.400">
            {error}
          </Text>
        </Center>
        <MoreButton onClick={() => handleOpenGuides()} />
      </Box>
    );
  }

  return (
    // Tight bottom padding: the footer logo follows directly, and the gap
    // between them was reading as dead space.
    <Box px={{ base: 4, md: 0 }} pt={4} pb={0}>
      <SectionHeader showLink={true} />

      {/* One row of 2, like Services — the preview holds property sections
          as well as guides. The bottom margin only separates the cards from
          the phone-only button below them. */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(2, 1fr)"
        gap={{ base: 4, md: 5 }}
        mb={{ base: 4, md: 0 }}
      >
        {previewItems.map((item) => {
          const cardImage =
            item.kind === "property" ? item.images?.[0] : imageUrls[item.id];

          return (
            <Card
              key={item.id}
              direction={"column"}
              overflow="hidden"
              variant="outline"
              bg="rgba(255, 255, 255, 0.05)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.1)"
              borderRadius="12px"
              cursor="pointer"
              transition="all 0.3s ease"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                borderColor: 'rgba(129, 126, 255, 0.3)',
              }}
              onClick={() => handleItemClick(item)}
            >
              {/* Imageless entries (most property sections) get a tinted
                  header instead of a broken placeholder request. */}
              {cardImage ? (
                <Image
                  objectFit="cover"
                  h={{ base: "95px", md: "160px" }}
                  w="full"
                  src={cardImage}
                  alt={item.title}
                />
              ) : (
                <Center
                  h={{ base: "95px", md: "160px" }}
                  w="full"
                  bg="rgba(129, 126, 255, 0.12)"
                >
                  <FaBookOpen color="#817EFF" size={22} />
                </Center>
              )}
              <CardBody p={3} pt={4}>
                <Heading size="md" color="white" fontSize="16px" fontWeight="700" mb={2}>
                  {item.title}
                </Heading>
                <Text fontSize="12px" color="gray.300" noOfLines={2} mb={2}>
                  {item.excerpt ?? item.description}
                </Text>
                {item.catalog?.name && (
                  <Badge
                    colorScheme="purple"
                    variant="subtle"
                    fontSize="9px"
                    px={2}
                    py={1}
                  >
                    {item.catalog.name}
                  </Badge>
                )}
              </CardBody>
            </Card>
          );
        })}
      </Box>

      <MoreButton onClick={() => handleOpenGuides()} />

      <GuideDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        guide={selectedGuide}
      />

      <PropertySectionDrawer
        isOpen={sectionOpen}
        onClose={handleSectionClose}
        section={selectedSection}
      />
    </Box>
  );
}; 